import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';
import './BuyCrystalsModal.css';

interface BuyCrystalsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const BuyCrystalsModal: React.FC<BuyCrystalsModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [amount, setAmount] = useState<string>('100');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const MIN_AMOUNT = 10;
  const MAX_AMOUNT = 10000;

  // Управление скроллом body при открытии/закрытии модалки
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const numericAmount = parseInt(amount) || 0;
  const isValidAmount = numericAmount >= MIN_AMOUNT && numericAmount <= MAX_AMOUNT;
  const priceRub = numericAmount; // 1 кристалл = 1 рубль

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Только цифры
    setAmount(value);
    setError(null);
  };

  const handleQuickAmount = (value: number) => {
    setAmount(value.toString());
    setError(null);
  };

  const handlePurchase = async () => {
    if (!isValidAmount || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.createPaymentSBP(numericAmount);

      if (response.success && response.payment_url) {
        // Открываем страницу оплаты через Telegram.openLink
        if (window.Telegram?.WebApp?.openLink) {
          window.Telegram.WebApp.openLink(response.payment_url);
        } else {
          // Fallback для случая вне Telegram
          window.open(response.payment_url, '_blank');
        }
        
        onSuccess?.();
        handleClose();
      } else {
        setError(response.message || 'Не удалось создать платеж');
      }
    } catch (err: any) {
      console.error('Payment creation error:', err);
      setError(err.response?.data?.message || 'Произошла ошибка при создании платежа');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setAmount('100');
    setError(null);
    onClose();
  };

  return (
    <div className="buy-crystals-overlay" onClick={handleClose}>
      <div className="buy-crystals-modal" onClick={(e) => e.stopPropagation()}>
        <button className="buy-crystals-close" onClick={handleClose}>
          ✕
        </button>

        <div className="buy-crystals-header">
          <h2 className="buy-crystals-title">💎 Пополнить баланс</h2>
          <p className="buy-crystals-subtitle">
            1 кристалл = 1 ₽
          </p>
        </div>

        <div className="buy-crystals-content">
          <div className="amount-input-section">
            <label className="amount-label">Количество кристаллов</label>
            <div className="amount-input-wrapper">
              <span className="amount-icon">💎</span>
              <input
                type="text"
                inputMode="numeric"
                value={amount}
                onChange={handleAmountChange}
                className={`amount-input ${!isValidAmount && amount ? 'invalid' : ''}`}
                placeholder="Введите количество"
                maxLength={5}
              />
            </div>
            <div className="amount-limits">
              от {MIN_AMOUNT} до {MAX_AMOUNT.toLocaleString()} кристаллов
            </div>
          </div>

          <div className="quick-amounts">
            <button
              className={`quick-amount-btn ${amount === '50' ? 'active' : ''}`}
              onClick={() => handleQuickAmount(50)}
            >
              50
            </button>
            <button
              className={`quick-amount-btn ${amount === '100' ? 'active' : ''}`}
              onClick={() => handleQuickAmount(100)}
            >
              100
            </button>
            <button
              className={`quick-amount-btn ${amount === '500' ? 'active' : ''}`}
              onClick={() => handleQuickAmount(500)}
            >
              500
            </button>
            <button
              className={`quick-amount-btn ${amount === '1000' ? 'active' : ''}`}
              onClick={() => handleQuickAmount(1000)}
            >
              1000
            </button>
            <button
              className={`quick-amount-btn ${amount === '5000' ? 'active' : ''}`}
              onClick={() => handleQuickAmount(5000)}
            >
              5000
            </button>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="price-summary">
            <div className="price-row">
              <span className="price-label">Кристаллы:</span>
              <span className="price-value">💎 {numericAmount.toLocaleString()}</span>
            </div>
            <div className="price-row total">
              <span className="price-label">К оплате:</span>
              <span className="price-value">{priceRub.toLocaleString()} ₽</span>
            </div>
          </div>
        </div>

        <button
          className="buy-crystals-submit"
          onClick={handlePurchase}
          disabled={!isValidAmount || isLoading}
        >
          {isLoading ? 'Создание платежа...' : `Оплатить ${priceRub.toLocaleString()} ₽`}
        </button>

        <p className="payment-note">
          Оплата через СБП. После нажатия откроется страница оплаты.
        </p>
      </div>
    </div>
  );
};

